import { ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import "./scroll.scss";
import { BackToTop } from "./back-to-top";
import { useI18n } from "../../i18n";

const LenisContext = createContext<Lenis | null | undefined>(undefined);

export function useLenis() {
  const context = useContext(LenisContext);
  if (context === undefined) {
    throw new Error('useLenis must be used within a Lenis provider');
  }
  return context;
}

interface ScrollProps {
  children: ReactNode;
}
export function Scroll({ children }: ScrollProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const [lenis, setLenis] = useState<Lenis | null>(null);
  const { language } = useI18n();

  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;

    const lenisInstance = new Lenis({
      wrapper: wrapperRef.current,
      content: contentRef.current,
      syncTouch: true,
      smoothWheel: true,
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      gestureOrientation: 'vertical',
    });

    setLenis(lenisInstance);

    const updateLenis = () => {
      if (typeof (lenisInstance as any).resize === "function") {
        (lenisInstance as any).resize();
      }
    };

    const animate = (time: number) => {
      lenisInstance.raf(time);
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    window.addEventListener('resize', updateLenis);

    const imageLoadListener = () => updateLenis();

    const attachImageListeners = (root: HTMLElement) => {
      root.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', imageLoadListener, { once: true });
        }
      });
    };

    attachImageListeners(contentRef.current);

    // Staggered timeouts to catch lazy-rendered content on first load
    const t1 = window.setTimeout(updateLenis, 100);
    const t2 = window.setTimeout(updateLenis, 500);
    const t3 = window.setTimeout(updateLenis, 1500);

    // Catch fonts finishing after mount (common cause of height changes on first visit)
    document.fonts?.ready.then(updateLenis);

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateLenis);
      resizeObserver.observe(contentRef.current);
      resizeObserverRef.current = resizeObserver;
    }

    // Attach load listeners to images added after initial mount (lazy/async components)
    let mutationObserver: MutationObserver | null = null;
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        if (contentRef.current) attachImageListeners(contentRef.current);
        updateLenis();
      });
      mutationObserver.observe(contentRef.current, { childList: true, subtree: true });
    }

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
      window.removeEventListener('resize', updateLenis);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      resizeObserverRef.current?.disconnect();
      mutationObserver?.disconnect();
      lenisInstance.destroy();
      setLenis(null);
    };
  }, []);

  // Update Lenis when language changes (content might change height)
  useEffect(() => {
    if (lenis) {
      // Small delay to ensure DOM has updated with new language content
      const timeoutId = setTimeout(() => {
        if (typeof (lenis as any).resize === "function") {
          (lenis as any).resize();
        }
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [language, lenis]);

  return (
    <LenisContext.Provider value={lenis}>
      <div className="scroll-main" ref={wrapperRef}>
        <div className="scroll-content" ref={contentRef}>
          {children}
        </div>
      </div>
      {lenis && <BackToTop lenis={lenis} />}
    </LenisContext.Provider>
  );
}